import { KeyValuePipe } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { DeviceTypeParameterConfigWithAvailableListAndParameter } from '@vectronic/util';

@Component({
  selector: 'vectronic-parameter-pattern-input-config',
  templateUrl: './parameter-pattern-input-config.component.html',
  styleUrls: ['./parameter-pattern-input-config.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatTooltip,
    MatIconButton,
    FormsModule,
    KeyValuePipe,
    MatOption,
    MatSelect,
  ],
})
export class ParameterPatternInputConfigComponent {
  config = input<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  resetToFactoryTooltip = input<string>();

  hideLabel = input<boolean>();
  hasClose = input(false);
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();
  patterns = input<Record<string, string>>();

  pulseType = signal<string>(undefined);
  pulseLength = signal<string>(undefined);
  periodLength = signal<string>(undefined);

  configClose = output();
  parameterChange = output<{
    config: DeviceTypeParameterConfigWithAvailableListAndParameter;
    value: string;
  }>();
  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();

  ranges = computed(() => {
    const validString = this.config()?.parameter?.validValueRange?.bytewise;
    return validString?.split(';').map((value) => {
      if (value.includes('..')) {
        const [min, max] = value.split('..').map(Number);
        return { min, max };
      }
      const num = Number(value);
      return { min: num, max: num };
    });
  });

  pulseLengthMin = computed(() => this.ranges()?.[1]?.min);
  pulseLengthMax = computed(() => this.ranges()?.[1]?.max);
  periodLengthMin = computed(() => this.ranges()?.[2]?.min);
  periodLengthMax = computed(() => this.ranges()?.[2]?.max);

  defaultPulseType = computed(() => {
    if (!this.value()) return '';
    const values = this.value().split(',');
    return values?.[0] || '';
  });

  defaultPulseLength = computed(() => {
    if (!this.value()) return '';
    const values = this.value().split(',');
    return values?.[1] || '';
  });

  defaultPeriodLength = computed(() => {
    if (!this.value()) return '';
    const values = this.value().split(',');
    return values?.[2] || '';
  });

  changedPulseType = computed(() => {
    if (this.pulseType() === undefined) return this.defaultPulseType();
    return this.pulseType() || '';
  });

  changedPulseLength = computed(() => {
    if (this.pulseLength() === undefined) return this.defaultPulseLength();
    return this.pulseLength() || '';
  });

  changedPeriodLength = computed(() => {
    if (this.periodLength() === undefined) return this.defaultPeriodLength();
    return this.periodLength() || '';
  });

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.pulseType.set('');
    this.pulseLength.set('');
    this.periodLength.set('');
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
    this.pulseType.set(undefined);
    this.pulseLength.set(undefined);
    this.periodLength.set(undefined);
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
    this.pulseType.set(undefined);
    this.pulseLength.set(undefined);
    this.periodLength.set(undefined);
  }

  onChange() {
    if (this.changedPulseType() && this.changedPulseLength() && this.changedPeriodLength()) {
      this.parameterChange.emit({
        config: this.config(),
        value: `${this.changedPulseType()},${this.changedPulseLength()},${this.changedPeriodLength()}`,
      });
    }
  }

  onChangePulseLength($event: string) {
    if (this.pulseLengthMin() && parseInt($event) < this.pulseLengthMin()) {
      this.pulseLength.set(this.pulseLengthMin().toString());
    } else if (this.pulseLengthMax() && parseInt($event) > this.pulseLengthMax()) {
      this.pulseLength.set(this.pulseLengthMax().toString());
    } else {
      this.pulseLength.set($event);
    }
    this.onChange();
  }

  onChangePeriodLength($event: string) {
    if (this.periodLengthMin() && parseInt($event) < this.periodLengthMin()) {
      this.periodLength.set(this.periodLengthMin().toString());
    } else if (this.periodLengthMin() && parseInt($event) > this.periodLengthMax()) {
      this.periodLength.set(this.periodLengthMax().toString());
    } else {
      this.periodLength.set($event);
    }
    this.onChange();
  }

  onChangePulseType($event: MatSelectChange) {
    this.pulseType.set($event.value);
    this.onChange();
  }
}
